import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

export interface AdoptionSummary {
  totalEvents: number;
  uniqueUsers: number;
  dau: number;
  mau: number;
  distinctFeatures: number;
}

export interface FeatureUsage {
  featureKey: string;
  eventCount: number;
  uniqueUsers: number;
}

export interface PersonaUsage {
  persona: string;
  uniqueUsers: number;
  eventCount: number;
}

export interface ActiveUser {
  employeeId: string | null;
  email: string | null;
  persona: string | null;
  eventCount: number;
  topFeature: string | null;
  lastSeen: string | null;
}

export interface AdoptionTrendPoint {
  bucket: string;
  activeUsers: number;
  events: number;
}

@Injectable({ providedIn: 'root' })
export class AdoptionService {
  constructor(private readonly http: HttpClient) {}

  getSummary(from?: string, to?: string): Observable<AdoptionSummary> {
    return this.http.get<AdoptionSummary>(
      `${environment.apiUrl}/admin/adoption/summary`,
      { params: this.range(from, to) },
    );
  }

  getByFeature(from?: string, to?: string, limit = 20): Observable<FeatureUsage[]> {
    return this.http.get<FeatureUsage[]>(
      `${environment.apiUrl}/admin/adoption/by-feature`,
      { params: this.range(from, to).set('limit', String(limit)) },
    );
  }

  getByPersona(from?: string, to?: string): Observable<PersonaUsage[]> {
    return this.http.get<PersonaUsage[]>(
      `${environment.apiUrl}/admin/adoption/by-persona`,
      { params: this.range(from, to) },
    );
  }

  getActiveUsers(from?: string, to?: string, limit = 100): Observable<ActiveUser[]> {
    return this.http.get<ActiveUser[]>(
      `${environment.apiUrl}/admin/adoption/active-users`,
      { params: this.range(from, to).set('limit', String(limit)) },
    );
  }

  getTrend(from?: string, to?: string, granularity: 'daily' | 'weekly' = 'daily'): Observable<AdoptionTrendPoint[]> {
    return this.http.get<AdoptionTrendPoint[]>(
      `${environment.apiUrl}/admin/adoption/trend`,
      { params: this.range(from, to).set('granularity', granularity) },
    );
  }

  private range(from?: string, to?: string): HttpParams {
    let params = new HttpParams();
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);
    return params;
  }
}
